/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable @typescript-eslint/no-namespace */
import { useNavigation } from "react-router";
import React, { useEffect, useState } from "react";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { useTranslation } from "react-i18next";
import countries from "~/data/countries.json";
import { InputField, SelectField, TextAreaField } from "~/features/landing/InputField";
import { TitleWithCaboCheck } from "~/features/landing/TitleWithCaboCheck";
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- the only way I could find to add google namespace
import type { APILoadingStatus } from "@vis.gl/react-google-maps";
import { Button } from "~/components/ui/button";
import { ArrowRightIcon } from "lucide-react";

const SERVICE_TYPE_IDS = ["CaboLAB", "CaboVAX", "CaboPOC"] as const;

export type AddressInfo = {
  street: string;
  streetNumber?: string;
  zip: string;
  city: string;
  country: string;
};

export type PharmacistStep2Prefill = {
  partner_name?: string;
  salutation?: string;
  title?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  street?: string;
  zip?: string;
  city?: string;
  country?: string;
  telephone?: string;
  referral_source?: string;
  referral_source_other?: string;
  apozin_short_code?: string;
  misc_info?: string;
  interested_service_types?: string[];
};

export function isValidServiceType(value: string): value is (typeof SERVICE_TYPE_IDS)[number] {
  return SERVICE_TYPE_IDS.includes(value as (typeof SERVICE_TYPE_IDS)[number]);
}

function useServiceTypes() {
  const { t } = useTranslation();

  return [
    {
      id: "CaboLAB",
      label: t("CaboLAB"),
      description: t("Labordiagnostik - Probenentnahme und Versand von medizinischen Proben"),
    },
    {
      id: "CaboVAX",
      label: t("CaboVAX"),
      description: t("Impfmanagement - Digitale Verwaltung und Dokumentation von Impfungen"),
    },
    {
      id: "CaboPOC",
      label: t("CaboPOC"),
      description: t("Point-of-Care Testing - Schnelltests direkt in der Apotheke durchführen"),
    },
  ];
}

function useReferralSources() {
  const { t } = useTranslation();

  return [
    t("Persönliche Empfehlung"),
    t("Apozin-Kooperation"),
    t("Erfa-Gruppe"),
    t("Direktansprache durch CaboCheck"),
    t("Veranstaltung / Messe"),
    t("Fachzeitschrift / Branchenmedium"),
    t("Fortbildung / Webinar"),
    t("Google-Suche"),
    t("Social Media"),
    t("Sonstiges"),
  ];
}

export function PharmacistFormContentStep2({
  error,
  hasSecondStep = false,
  prefill,
}: {
  error?: string;
  hasSecondStep: boolean;
  prefill?: PharmacistStep2Prefill;
}) {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";
  const [street, setStreet] = React.useState(prefill?.street ?? "");
  // Track interested service types
  const [interestedTypes, setInterestedTypes] = useState<string[]>(prefill?.interested_service_types ?? []);
  const [referralSource, setReferralSource] = useState(prefill?.referral_source ?? "");
  const serviceTypes = useServiceTypes();
  const referralSources = useReferralSources();
  const isLockedApozinReferral = prefill?.referral_source === t("Apozin-Kooperation");
  const isApozinReferral = referralSource === t("Apozin-Kooperation");
  const isOtherReferral = referralSource === t("Sonstiges");
  const { placesService, placePredictions, getPlacePredictions } = usePlacesService({
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string,
    options: { input: "", componentRestrictions: { country: "DE" }, types: ["address"] },
  });

  const ERROR_MESSAGES: Record<string, string> = {
    email_registered: t("Diese E-Mail-Adresse ist bereits registriert."),
    unknown_bundle_id: t("Unbekannter Anwendungsfehler."),
    missing_apozin_short_code: t("Bitte geben Sie den APOZIN-Kurzcode ein."),
    partner_name_too_long: t("Der Name der Apotheke darf maximal 50 Zeichen lang sein."),
  };

  return (
    <div>
      <div className="flex flex-col justify-center gap-5 pb-5">
        <TitleWithCaboCheck title={t("Entdecken Sie Ihre Möglichkeiten mit")} />
        <p className="text-[20px]">
          {t("Bitte registrieren Sie sich hier und wir werden uns umgehend bei Ihnen melden.")}
        </p>

        <div className="mt-4 flex flex-col">
          <div className="grid gap-4 pb-8 lg:grid-cols-4">
            <input
              type="text"
              name="nickname"
              autoComplete="off"
              tabIndex={-1}
              style={{ position: "absolute", left: "-9999px" }}
            />

            <InputField
              label={t("Name der Apotheke") + " *"}
              className="lg:col-span-2 xl:col-span-2"
              id="pharmocy"
              name="partner_name"
              defaultValue={prefill?.partner_name}
              maxLength={50}
              required
            />
            <SelectField
              id="salutation"
              name="salutation"
              defaultValue={prefill?.salutation ?? "Frau"}
              required
              label={t("Anrede") + " *"}
            >
              <option value="Frau">{t("Frau")}</option>
              <option value="Herr">{t("Herr")}</option>
            </SelectField>
            <InputField label={t("Titel")} id="title" name="title" defaultValue={prefill?.title} />
            <InputField
              id="firstName"
              name="firstName"
              required
              label={t("Vorname") + " *"}
              className="xl:col-span-2"
              defaultValue={prefill?.firstName}
            />
            <InputField
              id="lastName"
              name="lastName"
              required
              label={t("Nachname") + " *"}
              className="xl:col-span-2"
              defaultValue={prefill?.lastName}
            />
            <InputField
              label={t("E-Mail Adresse") + " *"}
              id="email"
              name="email"
              type="email"
              required
              className="lg:col-span-2 xl:col-span-4"
              defaultValue={prefill?.email}
            />

            <div className="relative lg:col-span-3">
              <InputField
                id="street"
                label={t("Straße und Hausnummer")}
                className="lg:col-span-3 xl:col-span-2"
                type="text"
                name="street"
                value={street}
                autoComplete="off"
                onChange={(e) => {
                  getPlacePredictions({ input: e.target.value });
                  return setStreet(e.target.value);
                }}
              />
              {placePredictions?.length ? (
                <Suggestions
                  getPlacePredictions={getPlacePredictions}
                  predictions={placePredictions}
                  onClick={(prediction) => {
                    placesService?.getDetails({ placeId: prediction.place_id }, (placeDetails) => {
                      if (!placeDetails) return;
                      setStreet(getStreet(placeDetails) ?? "");
                      (document.getElementById("zip")! as HTMLInputElement).value = getZip(placeDetails) ?? "";
                      (document.getElementById("city")! as HTMLInputElement).value = getCity(placeDetails) ?? "";
                      getPlacePredictions({ input: "" });
                    });
                  }}
                />
              ) : null}
            </div>
            <InputField label={t("Postleitzahl")} type="text" name="zip" id="zip" defaultValue={prefill?.zip} />
            <InputField label={t("Stadt")} type="text" name="city" id="city" defaultValue={prefill?.city} />
            <SelectField id="country" name="country" label={t("Land")} defaultValue={prefill?.country ?? "DE"}>
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </SelectField>

            <InputField
              label={t("Telefonnummer")}
              id="telephone"
              name="telephone"
              type="tel"
              minLength={5}
              pattern="^[+]?[0-9]+$"
              title={t(
                "Bitte geben Sie eine gültige Telefonnummer ein (einschließlich Ländervorwahl, falls zutreffend)",
              )}
              className="lg:col-span-2 xl:col-span-3"
              defaultValue={prefill?.telephone}
            />
            <div className="lg:col-span-4">
              {isLockedApozinReferral ? <input type="hidden" name="referral_source" value={referralSource} /> : null}
              <SelectField
                id="referral_source"
                name="referral_source"
                label={t("Wie sind Sie auf uns aufmerksam geworden?")}
                value={referralSource}
                disabled={isLockedApozinReferral}
                onChange={(e) => setReferralSource(e.target.value)}
              >
                <option value="">{t("Bitte wählen...")}</option>
                {referralSources.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </SelectField>
            </div>
            {isApozinReferral ? (
              <>
                {isLockedApozinReferral ? (
                  <input type="hidden" name="apozin_short_code" value={prefill?.apozin_short_code ?? ""} />
                ) : null}
                <InputField
                  label={t("APOZIN-Kurzcode") + " *"}
                  id="apozin_short_code"
                  name={isLockedApozinReferral ? undefined : "apozin_short_code"}
                  className="lg:col-span-4"
                  defaultValue={prefill?.apozin_short_code}
                  required
                  readOnly={isLockedApozinReferral}
                />
              </>
            ) : null}
            {isOtherReferral ? (
              <TextAreaField
                label={t("Bitte nennen Sie uns den sonstigen Anlass")}
                id="referral_source_other"
                name="referral_source_other"
                className="lg:col-span-4"
                defaultValue={prefill?.referral_source_other}
                required
              />
            ) : null}
            <TextAreaField
              label={t("Zusätzliche Informationen (z.B. wann unser Team Sie am besten erreichen kann)")}
              id="message"
              name="misc_info"
              className="lg:col-span-4"
              defaultValue={prefill?.misc_info}
            />
          </div>

          {/* Service Type Interest Selection */}
          <div className="mt-8">
            <h3 className="mb-2 text-xl font-semibold">{t("Woran sind Sie interessiert?")}</h3>
            <p className="mb-4 text-sm text-gray-600">
              {t("Wählen Sie die Dienstleistungen aus, die für Sie interessant sind (optional)")}
            </p>
            <div className="space-y-3">
              {serviceTypes.map((serviceType) => (
                <label
                  key={serviceType.id}
                  htmlFor={`service-${serviceType.id}`}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border p-4 hover:bg-gray-50"
                >
                  <input
                    id={`service-${serviceType.id}`}
                    type="checkbox"
                    className="mt-1 h-5 w-5 rounded border-gray-300"
                    checked={interestedTypes.includes(serviceType.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setInterestedTypes([...interestedTypes, serviceType.id]);
                      } else {
                        setInterestedTypes(interestedTypes.filter((t) => t !== serviceType.id));
                      }
                    }}
                  />
                  <div className="flex-1">
                    <div className="text-lg font-medium">{serviceType.label}</div>
                    <div className="mt-1 text-sm text-gray-600">{serviceType.description}</div>
                  </div>
                </label>
              ))}
            </div>
            {/* Hidden input for form submission */}
            <input type="hidden" name="interested_service_types" value={JSON.stringify(interestedTypes)} />
          </div>
        </div>
      </div>
      {error ? <p className="mb-4 text-red-500">{ERROR_MESSAGES[error] ?? error}</p> : null}
      <Button type="submit" disabled={isSubmitting}>
        {hasSecondStep ? <span>{t("Weiter (1/2)")}</span> : <span>{t("Ich habe Interesse")}</span>}
        <ArrowRightIcon className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}

function Suggestions({
  predictions,
  onClick,
  getPlacePredictions,
}: {
  predictions: google.maps.places.AutocompletePrediction[];
  onClick: (prediction: google.maps.places.AutocompletePrediction) => void;
  getPlacePredictions: (options: google.maps.places.AutocompletionRequest) => void;
}) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const div = document.getElementById("suggestions");
      if (!div?.contains(event.target as Node)) {
        console.log("Clicked outside the div!");
        getPlacePredictions({ input: "" });
      }
    }
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [getPlacePredictions]);

  return (
    <div className="absolute top-full space-y-2 border bg-white p-4 text-left" id="suggestions">
      {predictions.map((prediction) => (
        <button
          key={prediction.place_id}
          type="button"
          className="block w-full text-left"
          onClick={() => onClick(prediction)}
        >
          {prediction.description}
        </button>
      ))}
    </div>
  );
}

function getZip(place: google.maps.places.PlaceResult) {
  return place.address_components?.find((c) => c.types.includes("postal_code"))?.long_name;
}

function getCity(place: google.maps.places.PlaceResult) {
  return place.address_components?.find((c) => c.types.includes("locality"))?.long_name;
}

function getStreet(place: google.maps.places.PlaceResult) {
  const street = place.address_components?.find((c) => c.types.includes("route"))?.long_name ?? "";
  const streetNumber = place.address_components?.find((c) => c.types.includes("street_number"))?.long_name ?? "";
  return `${street} ${streetNumber}`;
}
